import { ItemSeverityLevel, ItemStatus, ItemType } from "@/models/Item"

export interface JiraImportResponse {
  externalAccountId: string
  externalProjectId: string
  id: string
  projectId: string
  teamId: string
}

export interface JiraImportData {
  accountId: string
  apiKey: string
  externalAccountId: string
  externalProjectId: string
  projectId: string
  teamId: string
  userName: string
}

export interface JiraUser {
  accountId: string
  accountType: string
  emailAddress: string
  avatarUrls: {
    "48x48": string
    "24x24": string
    "16x16": string
    "32x32": string
  }
  displayName: string
  active: boolean
  hasErrors: boolean
}

export interface Sort {
  empty: boolean
  sorted: boolean
  unsorted: boolean
}

export interface Pageable {
  sort: Sort
  offset: number
  pageNumber: number
  pageSize: number
  paged: boolean
  unpaged: boolean
}

export interface ContentItem {
  id: string
  externalAccountId: string
  externalProjectId: string
  startInitiatorId: string
  accountId: string
  projectId: string
  teamId: string
  syncEnabled: boolean
  status: string
}

export interface JiraImportDataFull {
  content: ContentItem[]
  pageable: Pageable
  last: boolean
  totalPages: number
  totalElements: number
  sort: Sort
  first: boolean
  size: number
  number: number
  numberOfElements: number
  empty: boolean
}

export interface TeamJiraSyncDto {
  team: TeamPreviewDto
  sync: JiraSyncEnabledDto
}

export interface JiraSyncEnabledDto {
  syncPsToJira: boolean
  syncJiraToPs: boolean
  isSyncEnabled(): boolean
}

export interface TeamPreviewDto {
  id: string
  name: string
}

export interface JiraPsToJiraSeverityMappingDto {
  jiraSeverity: string
  psSeverityLevel: ItemSeverityLevel
}

export interface JiraPsToJiraStatusMappingDto {
  jiraStatus: string
  psItemType: ItemType
  psStoryStatus: ItemStatus
}

export interface JiraIssueTypeMappingDto {
  jiraIssueType: string
  psItemType: ItemType
}

export interface JiraSprintNameTeamMappingDto {
  source: {
    sprintNamePattern: string
  }
  target: {
    teamId: string
  }
}

export class JiraTeamSettingDto {
  ignoreSprintDateOverlapError = false
  jiraSubTaskName = ""
  jiraToPsIssueTypeMap: { [key: string]: ItemType } = {}
  jiraToPsSeverityLevelMap: { [key: string]: ItemSeverityLevel } = {}
  jiraToPsStatusMap: { [key: string]: { [key: string]: ItemStatus } } = {}
  psToJiraIssueTypeMap: { [key: string]: string } = {}
  psToJiraSeverityLevelMap: { [key: string]: string } = {}
  psToJiraStatusMap: { [key: string]: { [key: string]: string } } = {}
  sprintIgnoreList: string[] = []
  sprintNameTeamMapping: JiraSprintNameTeamMappingDto[] = []

  constructor(props?: Partial<JiraTeamSettingDto>) {
    this.ignoreSprintDateOverlapError = props?.ignoreSprintDateOverlapError ?? false
    this.jiraSubTaskName = props?.jiraSubTaskName ?? ""
    this.jiraToPsIssueTypeMap = props?.jiraToPsIssueTypeMap ?? {}
    this.jiraToPsSeverityLevelMap = props?.jiraToPsSeverityLevelMap ?? {}
    this.jiraToPsStatusMap = props?.jiraToPsStatusMap ?? {}
    this.psToJiraIssueTypeMap = props?.psToJiraIssueTypeMap ?? {}
    this.psToJiraSeverityLevelMap = props?.psToJiraSeverityLevelMap ?? {}
    this.psToJiraStatusMap = props?.psToJiraStatusMap ?? {}
    this.sprintIgnoreList = props?.sprintIgnoreList ?? []
    this.sprintNameTeamMapping = props?.sprintNameTeamMapping ?? []
  }

  static create(props?: Partial<JiraTeamSettingDto>): JiraTeamSettingDto {
    return new JiraTeamSettingDto(props)
  }
}

export type JiraSyncDirection = "jiraToPs" | "psToJira"
export type JiraMappingType = "items" | "severity" | "status"
